import React from 'react'
import ReactMarkdown from 'react-markdown'
import gfm from 'remark-gfm'
import remarkIns from 'remark-ins'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import { preventBrowserTranslationInTree } from 'helpers/domMutationRecovery'

interface ReactMarkdownNewlineProps {
    children: string
    components?: any
    [key: string]: any
}

const styleEnabledTags = [
    'a',
    'b',
    'blockquote',
    'code',
    'dd',
    'del',
    'div',
    'dl',
    'dt',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'i',
    'ins',
    'li',
    'mark',
    'ol',
    'p',
    'pre',
    's',
    'small',
    'span',
    'strong',
    'sub',
    'sup',
    'table',
    'tbody',
    'td',
    'tfoot',
    'th',
    'thead',
    'tr',
    'u',
    'ul',
]

const styleAttributes = styleEnabledTags.reduce((acc, tag) => ({
    ...acc,
    [tag]: [
        ...(defaultSchema.attributes?.[tag] || []),
        'style',
    ],
}), {})

const htmlSanitizeSchema = {
    ...defaultSchema,
    protocols: {
        ...defaultSchema.protocols,
        href: [...(defaultSchema.protocols?.href || []), 'tel'],
    },
    tagNames: [
        ...(defaultSchema.tagNames || []),
        'span',
    ],
    attributes: {
        ...defaultSchema.attributes,
        ...styleAttributes,
    },
}

const allowedStyleProperties = new Set([
    'background-color',
    'background',
    'border',
    'border-bottom',
    'border-bottom-color',
    'border-bottom-left-radius',
    'border-bottom-right-radius',
    'border-bottom-style',
    'border-bottom-width',
    'border-collapse',
    'border-color',
    'border-left',
    'border-left-color',
    'border-left-style',
    'border-left-width',
    'border-radius',
    'border-right',
    'border-right-color',
    'border-right-style',
    'border-right-width',
    'border-spacing',
    'border-style',
    'border-top',
    'border-top-color',
    'border-top-left-radius',
    'border-top-right-radius',
    'border-top-style',
    'border-top-width',
    'border-width',
    'box-shadow',
    'caption-side',
    'clear',
    'column-gap',
    'color',
    'display',
    'align-content',
    'align-items',
    'align-self',
    'flex-direction',
    'flex-wrap',
    'float',
    'font-family',
    'font-size',
    'font-style',
    'font-weight',
    'gap',
    'height',
    'justify-content',
    'justify-items',
    'justify-self',
    'letter-spacing',
    'line-height',
    'list-style-position',
    'list-style-type',
    'margin',
    'margin-bottom',
    'margin-left',
    'margin-right',
    'margin-top',
    'max-height',
    'max-width',
    'min-height',
    'min-width',
    'opacity',
    'outline',
    'outline-color',
    'outline-offset',
    'outline-style',
    'outline-width',
    'overflow-wrap',
    'object-fit',
    'object-position',
    'padding',
    'padding-bottom',
    'padding-left',
    'padding-right',
    'padding-top',
    'row-gap',
    'text-align',
    'text-decoration',
    'text-decoration-color',
    'text-decoration-line',
    'text-decoration-style',
    'text-indent',
    'text-shadow',
    'text-transform',
    'vertical-align',
    'white-space',
    'width',
    'word-break',
    'word-spacing',
])

const safeColorValue = /^(#[0-9a-f]{3,8}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)|hsla?\(\s*\d{1,3}(?:deg)?\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)|[a-z]+|var\(--[a-z0-9-_]+\))$/i
const safeFontWeightValue = /^(normal|bold|bolder|lighter|[1-9]00)$/i
const safeFontStyleValue = /^(normal|italic|oblique)$/i
const safeTextDecorationValue = /^(none|underline|line-through|overline)(\s+(none|underline|line-through|overline))*$/i
const safeFontFamilyValue = /^[-a-z0-9\s"',]+$/i
const safeLengthValue = /^(auto|0|-?\d*\.?\d+(px|em|rem|%|vh|vw|vmin|vmax|ch|ex|pt|pc|in|cm|mm))$/i
const safeLineHeightValue = /^(normal|0|\d*\.?\d+|-?\d*\.?\d+(px|em|rem|%|vh|vw|vmin|vmax|ch|ex|pt|pc|in|cm|mm))$/i
const safeNumberValue = /^(0|1|0?\.\d+)$/
const safeKeywordValue = /^[a-z-]+$/i

const colorProperties = new Set([
    'background',
    'background-color',
    'border-bottom-color',
    'border-color',
    'border-left-color',
    'border-right-color',
    'border-top-color',
    'color',
    'outline-color',
    'text-decoration-color',
])

const lengthProperties = new Set([
    'border-bottom-left-radius',
    'border-bottom-right-radius',
    'border-bottom-width',
    'border-left-width',
    'border-right-width',
    'border-top-left-radius',
    'border-top-right-radius',
    'border-top-width',
    'column-gap',
    'gap',
    'height',
    'letter-spacing',
    'margin-bottom',
    'margin-left',
    'margin-right',
    'margin-top',
    'max-height',
    'max-width',
    'min-height',
    'min-width',
    'outline-offset',
    'outline-width',
    'padding-bottom',
    'padding-left',
    'padding-right',
    'padding-top',
    'row-gap',
    'text-indent',
    'width',
    'word-spacing',
])

const multiLengthProperties = new Set([
    'border-radius',
    'border-spacing',
    'border-width',
    'margin',
    'padding',
])

const borderProperties = new Set([
    'border',
    'border-bottom',
    'border-left',
    'border-right',
    'border-top',
    'outline',
])

const keywordValues: { [key: string]: Set<string> } = {
    'align-content': new Set(['normal', 'start', 'end', 'center', 'stretch', 'space-between', 'space-around', 'space-evenly', 'flex-start', 'flex-end']),
    'align-items': new Set(['normal', 'start', 'end', 'center', 'stretch', 'baseline', 'flex-start', 'flex-end']),
    'align-self': new Set(['auto', 'normal', 'start', 'end', 'center', 'stretch', 'baseline', 'flex-start', 'flex-end']),
    'border-collapse': new Set(['collapse', 'separate']),
    'border-style': new Set(['none', 'hidden', 'dotted', 'dashed', 'solid', 'double']),
    'border-bottom-style': new Set(['none', 'hidden', 'dotted', 'dashed', 'solid', 'double']),
    'border-left-style': new Set(['none', 'hidden', 'dotted', 'dashed', 'solid', 'double']),
    'border-right-style': new Set(['none', 'hidden', 'dotted', 'dashed', 'solid', 'double']),
    'border-top-style': new Set(['none', 'hidden', 'dotted', 'dashed', 'solid', 'double']),
    'caption-side': new Set(['top', 'bottom']),
    'clear': new Set(['none', 'left', 'right', 'both']),
    'display': new Set(['block', 'inline', 'inline-block', 'flex', 'inline-flex', 'table', 'table-row', 'table-cell']),
    'flex-direction': new Set(['row', 'row-reverse', 'column', 'column-reverse']),
    'flex-wrap': new Set(['nowrap', 'wrap', 'wrap-reverse']),
    'float': new Set(['none', 'left', 'right']),
    'justify-content': new Set(['normal', 'start', 'end', 'center', 'stretch', 'space-between', 'space-around', 'space-evenly', 'flex-start', 'flex-end']),
    'justify-items': new Set(['normal', 'start', 'end', 'center', 'stretch']),
    'justify-self': new Set(['auto', 'normal', 'start', 'end', 'center', 'stretch']),
    'list-style-position': new Set(['inside', 'outside']),
    'list-style-type': new Set(['none', 'disc', 'circle', 'square', 'decimal', 'lower-alpha', 'upper-alpha', 'lower-roman', 'upper-roman']),
    'object-fit': new Set(['contain', 'cover', 'fill', 'none', 'scale-down']),
    'outline-style': new Set(['none', 'dotted', 'dashed', 'solid', 'double']),
    'overflow-wrap': new Set(['normal', 'break-word', 'anywhere']),
    'text-align': new Set(['left', 'right', 'center', 'justify', 'start', 'end']),
    'text-decoration-line': new Set(['none', 'underline', 'line-through', 'overline']),
    'text-decoration-style': new Set(['solid', 'double', 'dotted', 'dashed', 'wavy']),
    'text-transform': new Set(['none', 'capitalize', 'uppercase', 'lowercase']),
    'vertical-align': new Set(['baseline', 'sub', 'super', 'text-top', 'text-bottom', 'middle', 'top', 'bottom']),
    'white-space': new Set(['normal', 'nowrap', 'pre', 'pre-wrap', 'pre-line', 'break-spaces']),
    'word-break': new Set(['normal', 'break-all', 'keep-all', 'break-word']),
}

function tokenizeCssValue(value: string) {
    const tokens: string[] = []
    let current = ''
    let depth = 0
    for (const char of value) {
        if (char === '(') {
            depth += 1
        } else if (char === ')') {
            depth -= 1
        }
        if (/\s/.test(char) && depth === 0) {
            if (current) {
                tokens.push(current)
                current = ''
            }
        } else {
            current += char
        }
    }
    if (current) {
        tokens.push(current)
    }
    return tokens
}

function isSafeLengthList(value: string, maxItems = 4) {
    const tokens = tokenizeCssValue(value)
    return tokens.length > 0 && tokens.length <= maxItems && tokens.every((token) => safeLengthValue.test(token))
}

function isSafeBorderValue(value: string) {
    if (value === 'none') {
        return true
    }
    const tokens = tokenizeCssValue(value)
    return tokens.length > 0 && tokens.length <= 4 && tokens.every((token) => (
        safeLengthValue.test(token)
        || safeColorValue.test(token)
        || keywordValues['border-style'].has(token.toLowerCase())
    ))
}

function isSafeShadowValue(value: string) {
    if (value === 'none') {
        return true
    }
    return value.split(',').every((shadow) => {
        const tokens = tokenizeCssValue(shadow.trim())
        return tokens.length > 0 && tokens.length <= 7 && tokens.every((token) => (
            token.toLowerCase() === 'inset'
            || safeLengthValue.test(token)
            || safeColorValue.test(token)
        ))
    })
}

function isSafeObjectPositionValue(value: string) {
    const positionKeywords = new Set(['left', 'right', 'top', 'bottom', 'center'])
    const tokens = tokenizeCssValue(value)
    return tokens.length > 0 && tokens.length <= 2 && tokens.every((token) => (
        safeLengthValue.test(token) || positionKeywords.has(token.toLowerCase())
    ))
}

function isSafeStyleValue(property: string, value: string) {
    if (!allowedStyleProperties.has(property)) {
        return false
    }
    if (/url\s*\(|expression\s*\(|javascript\s*:|behavior\s*:|-moz-binding|@import|[<>]/i.test(value)) {
        return false
    }
    if (colorProperties.has(property)) {
        return safeColorValue.test(value)
    }
    if (lengthProperties.has(property)) {
        return safeLengthValue.test(value)
    }
    if (multiLengthProperties.has(property)) {
        return isSafeLengthList(value)
    }
    if (borderProperties.has(property)) {
        return isSafeBorderValue(value)
    }
    if (property in keywordValues) {
        return keywordValues[property].has(value.toLowerCase())
    }
    switch (property) {
        case 'box-shadow':
        case 'text-shadow':
            return isSafeShadowValue(value)
        case 'font-family':
            return value.length <= 120 && safeFontFamilyValue.test(value)
        case 'font-size':
            return safeLengthValue.test(value) || /^(xx-small|x-small|small|medium|large|x-large|xx-large|smaller|larger)$/i.test(value)
        case 'font-weight':
            return safeFontWeightValue.test(value)
        case 'font-style':
            return safeFontStyleValue.test(value)
        case 'line-height':
            return safeLineHeightValue.test(value)
        case 'object-position':
            return isSafeObjectPositionValue(value)
        case 'opacity':
            return safeNumberValue.test(value)
        case 'text-decoration':
            return safeTextDecorationValue.test(value)
        default:
            return safeKeywordValue.test(value)
    }
}

function sanitizeInlineStyle(style: string) {
    return style
        .split(';')
        .map((declaration) => {
            const [rawProperty, ...rawValue] = declaration.split(':')
            const property = rawProperty?.trim().toLowerCase()
            const value = rawValue.join(':').trim()
            if (!property || !value || !isSafeStyleValue(property, value)) {
                return null
            }
            return `${property}: ${value}`
        })
        .filter(Boolean)
        .join('; ')
}

function rehypeSafeInlineStyles() {
    function visit(node) {
        if (node?.type === 'element' && node.properties?.style) {
            const style = sanitizeInlineStyle(String(node.properties.style))
            if (style) {
                node.properties.style = style
            } else {
                delete node.properties.style
            }
        }
        if (node?.children) {
            node.children.forEach(visit)
        }
    }

    return visit
}

// Browser translation rewrites text nodes with legacy <font> wrappers. React can
// then crash while reconciling the changed sibling tree. Keep React-owned
// markdown untranslated while leaving the rest of the application translatable.
function rehypePreventBrowserTranslation() {
    return preventBrowserTranslationInTree
}

function ReactMarkdownNewlineComponent(props: ReactMarkdownNewlineProps) {
    const { children, components, ...rest } = props
    const markdownProps: any = {
        remarkPlugins: [gfm, remarkIns],
        rehypePlugins: [rehypeRaw, [rehypeSanitize, htmlSanitizeSchema], rehypeSafeInlineStyles, rehypePreventBrowserTranslation],
        components,
        ...rest,
    }

    if (typeof children !== 'string') {
        return (
            <ReactMarkdown {...markdownProps}>
                {children}
            </ReactMarkdown>
        )
    }

    // Split on literal \n (backslash + n in the stored string) — each becomes an explicit <br>.
    // After splitting, escape any remaining lone backslashes so they render as a literal \
    // rather than being interpreted by markdown (e.g. a trailing \ in GFM produces a <br>).
    const parts = children.split('\\n')

    if (parts.length === 1) {
        // No \n found — escape lone backslashes and render normally
        const escaped = children.replace(/\\/g, '\\\\')
        return (
            <ReactMarkdown {...markdownProps}>
                {escaped}
            </ReactMarkdown>
        )
    }

    return (
        <>
            {parts.map((part, i) => {
                const escaped = part.replace(/\\/g, '\\\\')
                return (
                    <React.Fragment key={i}>
                        {escaped && (
                            <ReactMarkdown {...markdownProps}>
                                {escaped}
                            </ReactMarkdown>
                        )}
                        {i < parts.length - 1 && <br />}
                    </React.Fragment>
                )
            })}
        </>
    )
}

export const ReactMarkdownNewline = React.memo(ReactMarkdownNewlineComponent)
ReactMarkdownNewline.displayName = 'ReactMarkdownNewline'
